/**
 * `TRACE_animation_binding` — binding by name.
 *
 * Source channels bind to the instance by exact, case-sensitive string
 * equality, and by no other rule:
 *
 * - a node's key is its `name`, when no other NODE of the file has that name
 *   (other collections do not interfere);
 * - a morph target's key is its name — `extensions.TRACE_animation_binding.targetNames`
 *   on the mesh, else the de facto `extras.targetNames` — when no other target
 *   of the same mesh has it;
 * - a `weights` channel binds per morph target name to EVERY mesh of the
 *   instance that has a target of that name (a face split over head, teeth
 *   and eye meshes is driven as one), whatever node the source keyed it on;
 * - channels on the source's `root` node offset the binding node itself.
 *
 * Pure: no Babylon, no I/O.
 */

/** The extension name. */
export const TRACE_ANIMATION_BINDING = 'TRACE_animation_binding';

export type TrsPath = 'translation' | 'rotation' | 'scale';

const TRS_PATHS: readonly string[] = ['translation', 'rotation', 'scale'];
const INDEX = /^(0|[1-9][0-9]*)$/;
const VISIBLE = 'extensions/KHR_node_visibility/visible';

type JsonObject = Record<string, unknown>;

function asObject(value: unknown): JsonObject | undefined {
    return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as JsonObject : undefined;
}

function asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
}

/** Names (keys) of one file, computed once. */
export interface FileNames {
    /** Node name → index, for names no other node carries. */
    readonly nodeByName: ReadonlyMap<string, number>;
    /** Each node's key (its unique name), or null. */
    readonly nodeKeys: readonly (string | null)[];
    /** `nodes[i].mesh`. */
    readonly nodeMesh: readonly (number | undefined)[];
    /** Per mesh: each morph target's key, or null (unnamed, or named twice in the mesh). */
    readonly morphTargetNames: readonly (readonly (string | null)[])[];
    /** Per node: its authored TRS (glTF defaults when unauthored). */
    readonly nodeRest: readonly Record<TrsPath, number[]>[];
    /** Animation name → index, for names no other animation carries. */
    readonly animationKeys: readonly (string | null)[];
}

const isNumbers = (value: unknown, length: number): value is number[] =>
    Array.isArray(value) && value.length === length && value.every(v => typeof v === 'number');

/**
 * A node's static TRS: its authored `translation`/`rotation`/`scale`, else
 * the decomposed `matrix` (column-major, no shear per glTF), else the glTF
 * defaults.
 */
function staticTrs(node: JsonObject): Record<TrsPath, number[]> {
    const m = node.matrix;
    if (isNumbers(m, 16)) {
        const sx = Math.hypot(m[0], m[1], m[2]), sy = Math.hypot(m[4], m[5], m[6]), sz = Math.hypot(m[8], m[9], m[10]);
        const det = m[0] * (m[5] * m[10] - m[6] * m[9]) - m[4] * (m[1] * m[10] - m[2] * m[9]) + m[8] * (m[1] * m[6] - m[2] * m[5]);
        const fx = det < 0 ? -sx : sx;
        // Rotation matrix r[row][col] from the normalized columns.
        const r = (row: number, col: number) => m[col * 4 + row] / [fx, sy, sz][col];
        const trace = r(0, 0) + r(1, 1) + r(2, 2);
        let q: number[];
        if (trace > 0) {
            const k = 0.5 / Math.sqrt(trace + 1);
            q = [(r(2, 1) - r(1, 2)) * k, (r(0, 2) - r(2, 0)) * k, (r(1, 0) - r(0, 1)) * k, 0.25 / k];
        } else if (r(0, 0) > r(1, 1) && r(0, 0) > r(2, 2)) {
            const k = 2 * Math.sqrt(1 + r(0, 0) - r(1, 1) - r(2, 2));
            q = [0.25 * k, (r(0, 1) + r(1, 0)) / k, (r(0, 2) + r(2, 0)) / k, (r(2, 1) - r(1, 2)) / k];
        } else if (r(1, 1) > r(2, 2)) {
            const k = 2 * Math.sqrt(1 + r(1, 1) - r(0, 0) - r(2, 2));
            q = [(r(0, 1) + r(1, 0)) / k, 0.25 * k, (r(1, 2) + r(2, 1)) / k, (r(0, 2) - r(2, 0)) / k];
        } else {
            const k = 2 * Math.sqrt(1 + r(2, 2) - r(0, 0) - r(1, 1));
            q = [(r(0, 2) + r(2, 0)) / k, (r(1, 2) + r(2, 1)) / k, 0.25 * k, (r(1, 0) - r(0, 1)) / k];
        }
        return { translation: [m[12], m[13], m[14]], rotation: q, scale: [fx, sy, sz] };
    }
    return {
        translation: isNumbers(node.translation, 3) ? [...node.translation] : [0, 0, 0],
        rotation: isNumbers(node.rotation, 4) ? [...node.rotation] : [0, 0, 0, 1],
        scale: isNumbers(node.scale, 3) ? [...node.scale] : [1, 1, 1],
    };
}

function uniqueKeys(values: readonly unknown[]): (string | null)[] {
    const count = new Map<string, number>();
    for (const v of values) if (typeof v === 'string' && v !== '') count.set(v, (count.get(v) ?? 0) + 1);
    return values.map(v => (typeof v === 'string' && count.get(v) === 1 ? v : null));
}

function morphTargetNamesOf(mesh: unknown): (string | null)[] {
    const object = asObject(mesh) ?? {};
    const first = asObject(asArray(object.primitives)[0]);
    const count = asArray(first?.targets).length;
    const extension = asObject(asObject(object.extensions)?.[TRACE_ANIMATION_BINDING]);
    const list = extension?.targetNames !== undefined ? extension.targetNames : asObject(object.extras)?.targetNames;
    if (!Array.isArray(list) || list.length !== count) return new Array<string | null>(count).fill(null);
    return uniqueKeys(list);
}

export function resolveFileNames(json: unknown): FileNames {
    const root = asObject(json) ?? {};
    const nodes = asArray(root.nodes).map(n => asObject(n) ?? {});
    const nodeKeys = uniqueKeys(nodes.map(n => n.name));
    const nodeByName = new Map<string, number>();
    nodeKeys.forEach((k, i) => { if (k !== null) nodeByName.set(k, i); });
    return {
        nodeByName,
        nodeKeys,
        nodeMesh: nodes.map(n => (Number.isInteger(n.mesh) ? n.mesh as number : undefined)),
        morphTargetNames: asArray(root.meshes).map(morphTargetNamesOf),
        nodeRest: nodes.map(n => staticTrs(n)),
        animationKeys: uniqueKeys(asArray(root.animations).map(a => asObject(a)?.name)),
    };
}

/** A source channel, as it binds. */
export type NameChannel =
    | { kind: 'root'; channel: number; sampler: number; path: TrsPath }
    | { kind: 'node'; channel: number; sampler: number; node: string; path: TrsPath | 'visible' }
    | {
        kind: 'morph'; channel: number; sampler: number; target: string;
        /** Set when the output is a whole `weights` vector: this target takes component `index` of each `count`-wide element. */
        component?: { index: number; count: number };
    };

export interface NameDropped {
    channel: number;
    reason: 'unnamed' | 'root-unsupported' | 'unsupported' | 'invalid';
    detail: string;
}

export interface NameBoundAnimation {
    index: number;
    /** The source animation's unique name — the bound clip's name — or null (not selectable). */
    name: string | null;
    channels: NameChannel[];
    dropped: NameDropped[];
}

export interface NameBindResult {
    animations: NameBoundAnimation[];
    /** `root` was given but no node of the source has that name. */
    rootMissing: boolean;
}

/**
 * Every animation of a source file, as channels keyed by name.
 *
 * @param root `sources[].root`: the source node whose TRS channels offset the binding node.
 */
export function bindSourceByName(json: unknown, names: FileNames, root?: string): NameBindResult {
    const doc = asObject(json) ?? {};
    const rootIndex = root !== undefined ? names.nodeByName.get(root) : undefined;

    const weights = (
        channel: number, sampler: number, node: number, j: number | 'all',
        out: NameChannel[], dropped: NameDropped[],
    ): void => {
        const mesh = names.nodeMesh[node];
        const targets = mesh !== undefined ? names.morphTargetNames[mesh] : undefined;
        if (!targets || targets.length === 0) {
            dropped.push({ channel, reason: 'invalid', detail: `weights on /nodes/${node}, which has no morph targets` });
            return;
        }
        const unnamed: number[] = [];
        const one = (k: number, component?: { index: number; count: number }): void => {
            const name = targets[k];
            if (name === null || name === undefined) unnamed.push(k);
            else out.push({ kind: 'morph', channel, sampler, target: name, ...(component ? { component } : {}) });
        };
        if (j === 'all') targets.forEach((_, k) => one(k, { index: k, count: targets.length }));
        else one(j);
        if (unnamed.length) dropped.push({ channel, reason: 'unnamed', detail: `morph target(s) ${unnamed.join(', ')} of /meshes/${mesh} have no name` });
    };

    const animations = asArray(doc.animations).map((a, index): NameBoundAnimation => {
        const animation = asObject(a) ?? {};
        const channels: NameChannel[] = [];
        const dropped: NameDropped[] = [];
        asArray(animation.channels).forEach((c, channel) => {
            const ch = asObject(c) ?? {};
            const target = asObject(ch.target) ?? {};
            if (!Number.isInteger(ch.sampler)) {
                dropped.push({ channel, reason: 'invalid', detail: 'channel has no sampler' });
                return;
            }
            const sampler = ch.sampler as number;
            // Normalize core and pointer targets to (node, property).
            let node: number;
            let property: string;
            if (target.path !== 'pointer') {
                if (!Number.isInteger(target.node) || typeof target.path !== 'string') {
                    dropped.push({ channel, reason: 'invalid', detail: `core channel with target ${JSON.stringify(target)}` });
                    return;
                }
                node = target.node as number;
                property = target.path;
            } else {
                const pointer = asObject(asObject(target.extensions)?.KHR_animation_pointer)?.pointer;
                const seg = typeof pointer === 'string' ? pointer.split('/').slice(1) : [];
                if (seg[0] !== 'nodes' || !INDEX.test(seg[1] ?? '') || seg.length < 3) {
                    dropped.push({ channel, reason: 'unsupported', detail: typeof pointer === 'string' ? pointer : `pointer ${JSON.stringify(pointer)}` });
                    return;
                }
                node = Number(seg[1]);
                property = seg.slice(2).join('/');
            }
            const w = /^weights(?:\/(0|[1-9][0-9]*))?$/.exec(property);

            if (rootIndex !== undefined && node === rootIndex) {
                if (TRS_PATHS.includes(property)) channels.push({ kind: 'root', channel, sampler, path: property as TrsPath });
                else dropped.push({ channel, reason: 'root-unsupported', detail: `${property} on the root "${root}"` });
                return;
            }
            if (w) {
                weights(channel, sampler, node, w[1] !== undefined ? Number(w[1]) : 'all', channels, dropped);
                return;
            }
            const key = names.nodeKeys[node] ?? null;
            if (key === null) {
                dropped.push({ channel, reason: 'unnamed', detail: `/nodes/${node} has no unique name` });
                return;
            }
            if (TRS_PATHS.includes(property)) channels.push({ kind: 'node', channel, sampler, node: key, path: property as TrsPath });
            else if (property === VISIBLE) channels.push({ kind: 'node', channel, sampler, node: key, path: 'visible' });
            else dropped.push({ channel, reason: 'unsupported', detail: `/nodes/${node}/${property}` });
        });
        return { index, name: names.animationKeys[index] ?? null, channels, dropped };
    });

    return { animations, rootMissing: root !== undefined && rootIndex === undefined };
}
